/**
 * Vendor trading session state (design/gdd/crafting-economy.md's Economy
 * half). Mirrors npc-state.ts's `createStore` + fetch pattern and
 * raid-state.ts's REST-call shape.
 *
 * Cash/Gold balance is NOT tracked anywhere else client-side yet (checked —
 * game-state.ts has no balance field). Rather than inventing a global economy
 * store this feature doesn't need, `balance` lives here, populated from the
 * `sell`/`buy` REST responses AND kept live while the panel is open via a
 * scoped `economy:updated` socket subscription (started on `loadVendor`,
 * stopped on `closeVendor` — see `connectBalanceSync`/`disconnectBalanceSync`).
 *
 * Inventory (the Sell side's item list) is deliberately NOT duplicated into
 * `VendorState` — the vendor UI reads `gameStateStore.get().inventory`
 * directly. `VendorService.sell`/`buy` both ride the SAME `inventory:updated`
 * socket push every other inventory mutation already uses (via
 * `InventoryService.refreshAndEmit`), so game-state.ts's existing
 * subscription (see `connectGameStateSync`) keeps it fresh on its own — BUT
 * `sellItems`/`buyItem` below also write the response's own `sheet` into
 * `gameStateStore` directly, not just rely on that push arriving. Same
 * precedent as `game-state.ts`'s `equipItem`/`unequipItem`, which explicitly
 * refetch inventory rather than trust a same-service event that turned out
 * not to fire for that action — a dropped/delayed socket push here would
 * otherwise leave a stale Sell row that 409s on click, and the response
 * already has the fresh sheet for free (no extra request).
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { socket } from '../../core/network/socket';
import { gameStateStore } from './game-state';
import type {
  VendorState, VendorDetail, VendorBalance, SellResult, BuyResult, SellQuote, SellQuoteEntry,
} from './vendor-state.types';

const INITIAL_STATE: VendorState = {
  vendor: null, balance: null, busy: false, error: null, lastReceipt: null, sellQuotes: {},
};

export const vendorStateStore = createStore<VendorState>({ ...INITIAL_STATE });

let activeVendorKey: string | null = null;
let unsubscribeBalance: (() => void) | null = null;

function connectBalanceSync(): void {
  disconnectBalanceSync();
  const onBalanceUpdated = (balance: VendorBalance) => {
    vendorStateStore.set((s) => ({ ...s, balance }));
  };
  socket.on('economy:updated', onBalanceUpdated);
  unsubscribeBalance = () => socket.off('economy:updated', onBalanceUpdated);
}

/**
 * The wallet as the character sheet knows it.
 *
 * `softBalance`/`premiumBalance` are on `CharacterSheetActive` and are what
 * `hud.ts` paints; reading them here means the panel and the HUD cannot
 * disagree on open. Null while the sheet has not loaded — an unknown balance
 * must stay unknown rather than render as 0, which would gate every Buy button
 * as unaffordable.
 */
function characterBalance(): VendorBalance | null {
  const character = gameStateStore.get().character;
  if (character?.state !== 'ACTIVE') return null;
  return { cash: character.softBalance, gold: character.premiumBalance };
}

function disconnectBalanceSync(): void {
  unsubscribeBalance?.();
  unsubscribeBalance = null;
}

/** Opens a vendor panel: fetches its detail (name/category/derived stock) and starts the balance push. */
export async function loadVendor(vendorKey: string): Promise<void> {
  activeVendorKey = vendorKey;
  connectBalanceSync();
  vendorStateStore.set((s) => ({
    ...s,
    busy: true,
    error: null,
    lastReceipt: null,
    // Seeded from the character sheet the HUD already reads, NOT left null.
    // `balance` used to be populated only by a sell/buy response or an
    // `economy:updated` push, so a freshly opened stall printed
    // "Cash: — · Gold: —" beside a HUD showing the real numbers, and the buy
    // gate below could not tell whether the player could afford anything.
    // Found by looking at it, not by a test.
    balance: s.balance ?? characterBalance(),
  }));
  try {
    const vendor = await api.get<VendorDetail>(`/vendor/${vendorKey}`);
    vendorStateStore.set((s) => ({ ...s, vendor, busy: false }));
  } catch (err) {
    vendorStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t open the vendor.') }));
  }
  await refreshSellQuotes();
}

/**
 * Re-prices the Sell side from the server.
 *
 * Fetched separately from the vendor detail, and AWAITED after it rather than
 * in a `Promise.all`, because the two fail differently: a dead quote must
 * leave the panel open and merely priceless, while a dead detail means there
 * is no panel. Bundling them would let a quote failure blank the stall.
 *
 * Runs on open and after every sell/buy — the quote is a function of what the
 * player owns, so a sale invalidates the rest of the list (the sold rows are
 * gone; the others are unchanged, but proving that costs the same call).
 */
export async function refreshSellQuotes(): Promise<void> {
  if (!activeVendorKey) return;
  const key = activeVendorKey;
  try {
    const quote = await api.get<SellQuote>(`/vendor/${key}/quote`);
    // A response that arrives after the player has walked away (or opened a
    // different stall) must not repaint prices for the wrong vendor.
    if (activeVendorKey !== key) return;
    const byId: Record<string, SellQuoteEntry> = {};
    for (const entry of quote.items) byId[entry.userItemId] = entry;
    vendorStateStore.set((s) => ({ ...s, sellQuotes: byId }));
  } catch {
    // Deliberately silent and NON-destructive: a failed quote is a missing
    // price, not a broken stall, and `state.error` is the banner that tells
    // the player an ACTION failed. Existing quotes are kept — a stale price is
    // closer to the truth than no price, and the sale itself is server-priced
    // regardless.
  }
}

/**
 * Sells one or more owned, non-equipped items (CARRIED or HIDEOUT — see
 * backend `InventoryService.removeSellableItems`'s docstring for why not
 * HIDEOUT-only). The resulting inventory refresh arrives via the existing
 * `inventory:updated` socket push (game-state.ts) — nothing to do here beyond
 * recording the receipt/balance.
 */
export async function sellItems(userItemIds: string[]): Promise<void> {
  if (!activeVendorKey || userItemIds.length === 0) return;
  vendorStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<SellResult>(`/vendor/${activeVendorKey}/sell`, { userItemIds });
    gameStateStore.set((s) => ({ ...s, inventory: result.sheet }));
    vendorStateStore.set((s) => ({
      ...s,
      busy: false,
      balance: result.balance,
      lastReceipt: { kind: 'sell', message: sellReceipt(result) },
    }));
    await refreshSellQuotes();
  } catch (err) {
    vendorStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t complete the sale.') }));
  }
}

/**
 * "Sold Scrap Metal for 2 Cash." — the item by NAME, not a count.
 *
 * `SellResult.sold` has carried per-item `name` and `price` since the endpoint
 * shipped; the old string threw both away and printed "Sold 1 item(s) for 2
 * Cash", which reads as a bug at a batch size of one — and one is the only
 * batch size this panel ever sends.
 *
 * Exported for its own test: it is the only piece of this module that is pure.
 */
export function sellReceipt(result: SellResult): string {
  if (result.sold.length === 1) {
    const [item] = result.sold;
    return `Sold ${item.name} for ${item.price} Cash.`;
  }
  // A real multi-item batch names the items it can fit and totals the rest,
  // rather than falling back to a count the single-item case was fixed to
  // avoid. Nothing sends one today; `sellItems` takes an array, so it can.
  const names = result.sold.slice(0, 3).map((i) => i.name).join(', ');
  const more = result.sold.length - 3;
  const tail = more > 0 ? ` and ${more} more` : '';
  return `Sold ${names}${tail} for ${result.cashEarned} Cash.`;
}

/** Buys `qty` of `itemKey` — lands in the HIDEOUT pool (capacity-checked server-side). */
export async function buyItem(itemKey: string, qty: number): Promise<void> {
  if (!activeVendorKey || qty < 1) return;
  vendorStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<BuyResult>(`/vendor/${activeVendorKey}/buy`, { itemKey, qty });
    gameStateStore.set((s) => ({ ...s, inventory: result.sheet }));
    vendorStateStore.set((s) => ({
      ...s,
      busy: false,
      balance: result.balance,
      // The shelf count follows the buy (Janeks, 2026-10-19: "the trader still
      // lists 10 of 10 available"). Older servers omit `remaining`; then count
      // down locally from what the panel showed.
      vendor: s.vendor ? {
        ...s.vendor,
        stock: s.vendor.stock.map((e) => (e.itemKey === itemKey
          ? { ...e, remaining: result.remaining ?? Math.max(0, e.remaining - qty) }
          : e)),
      } : s.vendor,
      lastReceipt: { kind: 'buy', message: buyReceipt(itemKey, qty, result) },
    }));
    await refreshSellQuotes();
  } catch (err) {
    vendorStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t complete the purchase.') }));
  }
}

/**
 * "Bought 2 x Rifle Rounds for 30 Cash." — the same shape as the sell
 * receipt, for the same reason: `Bought 2 for 30 Cash` names no goods at all.
 *
 * The NAME comes from the open stall's stock rather than from the response,
 * which reports only how many rows landed (`added`). `qty` is what the player
 * asked for; `added` can differ for a stackable item, where N units merge into
 * fewer rows — so the quantity shown is the one the player typed.
 */
export function buyReceipt(itemKey: string, qty: number, result: BuyResult): string {
  const name = vendorStateStore.get().vendor?.stock.find((e) => e.itemKey === itemKey)?.name ?? itemKey;
  const count = qty > 1 ? `${qty} x ` : '';
  return `Bought ${count}${name} for ${result.cashSpent} Cash.`;
}

export function closeVendor(): void {
  activeVendorKey = null;
  disconnectBalanceSync();
  vendorStateStore.set({ ...INITIAL_STATE });
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
